/**
 * Order Calculations & Utilities
 *
 * Pure functions for order business logic.
 * Will be expanded as sections are built in later phases.
 */

// ===========================
// TYPE CHECKS
// ===========================

/**
 * Check if order is a crosstown type
 */
export const isCrosstown = (orderType) => {
  return orderType === 'crosstown'
}

/**
 * Check if order is an empty repo type
 */
export const isEmptyRepo = (orderType) => {
  return orderType === 'empty_repo'
}

/**
 * Check if order is a bobtail type
 */
export const isBobtail = (orderType) => {
  return orderType === 'bobtail'
}

// ===========================
// FORMATTING
// ===========================

/**
 * Format price as USD currency
 */
export const formatPrice = (price) => {
  if (price == null || isNaN(price)) return '$0.00'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(price)
}

/**
 * Format address as single line string
 */
export const formatAddress = (address) => {
  if (!address) return ''
  const { street1, street2, city, state, zip } = address
  const street = [street1, street2].filter(Boolean).join(' ')
  return `${street}, ${city}, ${state} ${zip}`
}

/**
 * Format date as locale string
 */
export const formatDate = (date) => {
  if (!date) return ''
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Format datetime as locale string
 */
export const formatDateTime = (datetime) => {
  if (!datetime) return ''
  return new Date(datetime).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

// ===========================
// PRICING CALCULATIONS
// ===========================
// Will be expanded in Phase 5 (Pricing Section)

/**
 * Calculate customer total (line haul + fuel + accessorials)
 * Placeholder - will be expanded in Phase 5
 */
export const calculateCustomerTotal = (order) => {
  const lineHaul = parseFloat(order.lineHaulPrice) || 0
  const fuel = parseFloat(order.fuelSurcharge) || 0
  const accessorialTotal =
    order.accessorials?.reduce(
      (sum, acc) => sum + (parseFloat(acc.price) || 0),
      0,
    ) || 0
  return lineHaul + fuel + accessorialTotal
}

/**
 * Calculate fuel surcharge from base price and percentage
 * Placeholder - will be expanded in Phase 5
 */
export const calculateFuelSurcharge = (basePrice, percentage) => {
  const base = parseFloat(basePrice) || 0
  const percent = parseFloat(percentage) || 0
  return (base * (percent / 100)).toFixed(2)
}

/**
 * Round number to fixed decimal precision
 * @param {number} n - Number to round
 * @param {number} precision - Decimal places (default: 2)
 * @returns {number} Rounded number
 */
export const toFixedFloat = (n, precision = 2) => {
  return Number.parseFloat(Number(n).toFixed(precision))
}

/**
 * Calculate fuel surcharge dollar amount from base price and percentage
 *
 * This is the canonical implementation used by lane pricing.
 * Returns raw number without rounding (for precision in calculations).
 *
 * @param {number} lineHaul - Base line haul price
 * @param {number} percent - Fuel surcharge percentage
 * @returns {number} Fuel surcharge amount in dollars
 */
export const computeFuelSurchargeDollarAmount = (lineHaul, percent) => {
  const base = parseFloat(lineHaul) || 0
  const percentage = parseFloat(percent) || 0
  return base * (percentage / 100)
}

/**
 * Calculate fuel surcharge percentage from line haul and fuel dollar amount
 * @param {number} lineHaulPrice - Base line haul price
 * @param {number} fuelSurcharge - Fuel surcharge in dollars
 * @returns {number} Percentage (e.g., 15.25 for 15.25%)
 */
export const computeFuelSurchargePercent = (lineHaulPrice, fuelSurcharge) => {
  const lineHaul = parseFloat(lineHaulPrice) || 0
  const fuel = parseFloat(fuelSurcharge) || 0
  if (!lineHaul) return 0
  return toFixedFloat((fuel / lineHaul) * 100)
}

/**
 * Format "other value" label for fuel surcharge toggle
 * Shows $ when % is active, and vice versa
 * @param {string} mode - Current mode ('dollarAmount' or 'percent')
 * @param {number} lineHaulPrice - Line haul price
 * @param {number} fuelSurcharge - Fuel surcharge in dollars
 * @returns {string} Formatted label (e.g., "$75" or "15%")
 */
export const formatFuelSurchargeOtherValue = (
  mode,
  lineHaulPrice,
  fuelSurcharge,
) => {
  if (!lineHaulPrice) return '--'

  const otherValue =
    mode === 'dollarAmount'
      ? computeFuelSurchargePercent(lineHaulPrice, fuelSurcharge)
      : parseFloat(fuelSurcharge) || 0

  // Guard against 0, NaN, null, undefined
  if (!otherValue) return '--'

  const formatted = toFixedFloat(otherValue).toFixed(2).replace(/\.00$/, '')
  if (!formatted || formatted === '0') return '--'

  return mode === 'dollarAmount' ? `${formatted}%` : `$${formatted}`
}

/**
 * Calculate broker cost (carrier price + fuel)
 * Placeholder - will be expanded in Phase 5
 */
export const calculateBrokerCost = (order) => {
  const carrierPrice =
    parseFloat(order.carrierPriceOverride || order.lineHaulPrice) || 0
  const fuel = parseFloat(order.fuelSurcharge) || 0
  return carrierPrice + fuel
}

// ===========================
// VALIDATION HELPERS
// ===========================

/**
 * Check if order can be tendered
 */
export const canOrderBeTendered = (order) => {
  return (
    order.state === 'draft' &&
    !!order.imcReferenceNumber &&
    order.legs?.length > 0
  )
}

/**
 * Check if order is in a finalized state
 */
export const isOrderFinalized = (order) => {
  return ['finished', 'closed', 'canceled'].includes(order.state)
}

/**
 * Check if order can be edited
 */
export const canOrderBeEdited = (order) => {
  return !isOrderFinalized(order)
}
